import "./UIDSSKUCodePromos.css";
import { Box, Button, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { ChannelkartNetworkGet } from "../../services/networkService";
import { EnvironmentRegistry } from "../../services/registry/environmentRegistry";
import { GenericPopUp } from "../../components/popup/genericPopUp";
import { popupType } from "../../types/types";
// import notFoundImage from "../../images/notFoundImage.svg";
import RenderSvg from "../../components/renderSvg/RenderSvg";
import { useState } from "react";

const searchSx = {
  "& .MuiInputBase-input": {
    alignItems: "center",
    display: "flex",
    height: "34px !important",
    padding: "10px 14px !important"
  },
  "& .MuiOutlinedInput-root": {
    alignItems: "center",
    background: "#fafbfc",
    borderRadius: "6px",
    display: "flex",
    fontSize: "15px",
    height: "38px !important",
    padding: 0
  },
  "& .MuiSelect-select": {
    alignItems: "center",
    background: "#fafbfc",
    borderRadius: "6px",
    display: "flex",
    height: "50px !important",
    padding: 0
  },
  "& .MuiSvgIcon-root": {
    alignItems: "center",
    background: "#fafbfc",
    borderRadius: "6px",
    display: "flex",
    height: 28,
    justifyContent: "center",
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    width: 28
  },
  background: "#fafbfc",
  borderRadius: "6px",
  height: "50px !important",
  width: "100%"
};

type Search = {
  sskuCode: string;
  uid: string;
};

type Promo = {
  creationDate: string;
  endDate: string;
  promoId: string;
  startDate: string;
};

const formatDateTimeFunction = (input: string): string => {
  const date = new Date(input);
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const onChangeFunction = <T extends object>(
  setter: React.Dispatch<React.SetStateAction<T>>,
  field: keyof T,
  value: T[keyof T]
): void => {
  setter(prev => ({ ...prev, [field]: value }));
};

const UIDSSKUCodePromos: React.FC = () => {
  const [genericModalMessage, setGenericModalMessage] = useState<string>("");
  const [genericModalState, setGenericModalState] = useState<boolean>(false);
  const [genericModalType, setGenericModalType] = useState<popupType>("Error");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [promos, setPromos] = useState<Promo[]>([]);
  const [search, setSearch] = useState<Search>({ sskuCode: "", uid: "" });
  const [showNotFound, setShowNotFound] = useState<boolean>(false);

  const openPopUpFunction = (message: string, modalType: popupType) => {
    setGenericModalMessage(message);
    setGenericModalType(modalType);
    setGenericModalState(true);
  };

  const onSubmitFunction = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!search.uid.length || !search.sskuCode.length) {
      openPopUpFunction("Both UID and SSKU Code must be entered", "Error");
      return;
    }

    const env = EnvironmentRegistry.getEnvironment() || "uat";
    const baseUrl: string = EnvironmentRegistry.getPromoUrl(env);
    const url: string = `${baseUrl}/api/v1/promos/fetch/forSKUCode?outletcode=${search.uid}&skucode=${search.sskuCode}`;

    setIsLoading(true);
    setShowNotFound(false);

    try {
      const response = await ChannelkartNetworkGet(url);
      const promosArray: Promo[] = response.data.data.map((item: Promo) => ({
        ...item,
        endDate: formatDateTimeFunction(item.endDate),
        startDate: formatDateTimeFunction(item.startDate)
      }));

      setPromos(promosArray);

      if (!promosArray.length) {
        openPopUpFunction("No promos found", "Alert");
        setShowNotFound(true);
      }
    } catch (error) {
      openPopUpFunction("No promos found", "Alert");
      setShowNotFound(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box className="promos-root">
        <Box
          sx={{
            background: "transparent",
            borderRadius: 2,
            boxShadow: "none",
            mb: 3,
            ml: 0,
            mt: { xs: 1, md: 2 },
            px: { xs: 2, md: 4 },
            py: 2,
            position: "relative",
            zIndex: 1
          }}
        >
          <Typography
            variant="h6"
            sx={{ color: "#23272f", fontSize: { xs: 20, md: 22 }, fontWeight: 600, m: 0, ml: { md: "-8px", xs: "8px" } }}
          >
            Schemes Based on UID X SSKU
          </Typography>
        </Box>

        <Paper
          className="promos-form-card"
          elevation={0}
          sx={{ p: { xs: 2, md: 3 } }}
          style={{ marginTop: "-26px" }}
        >
          <form onSubmit={onSubmitFunction}>
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              justifyContent="space-between"
              sx={{ mb: "-12px", mt: { md: "-10px", xs: "-2px" } }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: "#23272f", fontSize: 16, fontWeight: 600, mb: 1 }}>UID</Typography>
                <TextField
                  fullWidth
                  onChange={e => onChangeFunction(setSearch, "uid", e.target.value)}
                  placeholder="e.g. SCOITEST7"
                  size="small"
                  sx={{ ...searchSx }}
                  value={search.uid}
                />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: "#23272f", fontSize: 16, fontWeight: 600, mb: 1 }}>SSKU Code</Typography>
                <TextField
                  fullWidth
                  onChange={e => onChangeFunction(setSearch, "sskuCode", e.target.value)}
                  placeholder="e.g. FMB100BA"
                  size="small"
                  sx={{ ...searchSx }}
                  value={search.sskuCode}
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                <Button
                  disabled={isLoading}
                  sx={{
                    background: "#24c6b1",
                    borderRadius: 2,
                    boxShadow: "none",
                    fontSize: 15,
                    fontWeight: 600,
                    height: 38,
                    mb: { xs: "12px" },
                    px: 4,
                    py: 1.2,
                    textTransform: "none",
                    width: { xs: "100%", md: "120px" },
                    "&:hover": { background: "#1fae9e" }
                  }}
                  type="submit"
                  variant="contained"
                >
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : "Search"}
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>

        <Paper className="promos-table-card" elevation={0} sx={{ mt: "-6px", p: { xs: 2, md: 3 } }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: "#f7f8fa" }}>
                  <TableCell align="center" className="promos-th" sx={{ color: "#23272f", fontSize: 13, fontWeight: 300, p: "9px" }}>
                    S No
                  </TableCell>
                  <TableCell align="center" className="promos-th" sx={{ color: "#23272f", fontSize: 13, fontWeight: 300, p: "9px" }}>
                    Scheme ID
                  </TableCell>
                  <TableCell align="center" className="promos-th" sx={{ color: "#23272f", fontSize: 13, fontWeight: 300, p: "9px" }}>
                    Start Date & Time
                  </TableCell>
                  <TableCell align="center" className="promos-th" sx={{ color: "#23272f", fontSize: 13, fontWeight: 300, p: "9px" }}>
                    End Date & Time
                  </TableCell>
                </TableRow>
              </TableHead>

              {isLoading ? (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={4}>
                      <Box sx={{ alignItems: "center", display: "flex", height: "200px", justifyContent: "center" }}>
                        <CircularProgress />
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : showNotFound ? (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={4}>
                      <Box
                        sx={{
                          alignItems: "center",
                          display: "flex",
                          flexDirection: "column",
                          height: "150px",
                          justifyContent: "center",
                          marginBottom: "38px",
                          marginTop: "35px"
                        }}
                      >
                        <NotFoundImage />
                        <Typography sx={{ color: "#23272f", fontSize: 13, m: 1, pb: 0 }}>No Promos Found</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  {promos.map((promo, idx) => (
                    <TableRow key={idx} sx={{ background: "#fff" }}>
                      <TableCell align="center" sx={{ color: "#23272f", fontSize: 14, p: "9px" }}>
                        {idx + 1}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "#23272f", fontSize: 14, p: "9px" }}>
                        {promo.promoId}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "#23272f", fontSize: 14, p: "9px" }}>
                        {promo.startDate}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "#23272f", fontSize: 14, p: "9px" }}>
                        {promo.endDate}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Paper>

        <GenericPopUp
          message={genericModalMessage}
          openGenericModal={genericModalState}
          setOpenGenericModal={() => setGenericModalState(false)}
          type={genericModalType}
        />
      </Box>
    </>
  );
};

export default UIDSSKUCodePromos;

const NotFoundImage = ({ height = "80%", style = {} }) => (
  <div style={{ marginBottom: "5px", marginTop: "5px", maxHeight: height, objectFit: "contain", ...style, }}>
    <RenderSvg name="notFoundImage" />
  </div>
);
